// @workflow platform.viewer
/**
 * The viewer page's panes and its 360 entry point, registered by the page itself (MainView calls
 * `registerViewerPanes` in `created`), so the adapter never reaches into a component.
 * Today these live as MainView methods; when they move out of the view, the registration moves
 * with them and callers of the adapter do not change.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */

/**
 * What the page registers. Each function is bound to the page; none of them is the component.
 * @typedef {object} ViewerPanes
 * @property {(type: string) => *} openPane    open a pane of `type` beside the others; undefined when every pane is in use
 * @property {(type: string) => void} showPane show the hidden pane of `type`
 * @property {(slot: number, type: string) => boolean} changePane switch the pane in `slot` to `type`, as the
 *           user's own view switcher does; false when there is no such slot
 * @property {(tagId: *, options: { isWanted: () => boolean }) => Promise<*>} openTagIn360 the page's ?zoom360 pipeline
 */

const PANE_METHODS = Object.freeze(['openPane', 'showPane', 'changePane', 'openTagIn360']);

function assertPanes(panes) {
  const missing = PANE_METHODS.filter((name) => !panes || typeof panes[name] !== 'function');
  if (missing.length) throw new TypeError(`registerViewerPanes: missing ${missing.join(', ')}`);
}

/** One registry; the last page to register wins, and only that page can unregister itself. */
export function createPaneRegistry() {
  let current = null;
  return Object.freeze({
    /**
     * @param {ViewerPanes} panes
     * @returns {() => void} unregister: clears the registration only while it is still this one
     */
    register(panes) {
      assertPanes(panes);
      const entry = Object.freeze({ ...panes });
      current = entry;
      return () => {
        if (current === entry) current = null;
      };
    },
    /** @returns {ViewerPanes|null} */
    get() {
      return current;
    },
  });
}

/** The app's registry (one viewer page). */
export const viewerPanes = createPaneRegistry();

/** @param {ViewerPanes} panes @returns {() => void} unregister */
export const registerViewerPanes = (panes) => viewerPanes.register(panes);
